import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Loader2, Bell, Mail, Save, ArrowLeft, Lock, AlertCircle,
  CheckCircle2,
} from 'lucide-react';

import { Button, Card, Badge } from '../../ui-v2';
import { getPreferenciasNotif, actualizarPreferenciasNotif } from '../api';

/**
 * PreferenciasNotificaciones — el usuario configura qué notificaciones
 * quiere recibir y por qué canal (email / in-app).
 *
 * Ruta: /citas-tecnicas/preferencias-notificaciones
 * Permiso: solo authMiddleware (cualquier persona puede ver/editar
 *          sus propias preferencias).
 *
 * Las obligatorias se muestran con toggles deshabilitados + badge.
 */

export default function PreferenciasNotificaciones() {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [draft, setDraft] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [exito, setExito] = useState(false);

  const cargar = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await getPreferenciasNotif();
      const lista = res.data || [];
      setData(lista);
      setDraft(lista.map((p) => ({ ...p })));
    } catch (e) {
      console.error('[PreferenciasNotificaciones]', e);
      setError(e?.response?.data?.error || 'No se pudieron cargar las preferencias.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  function actualizar(tipo, campo, valor) {
    setDraft((prev) => prev.map((p) =>
      p.tipo === tipo ? { ...p, [campo]: valor } : p
    ));
    setError(null); setExito(false);
  }

  const dirty = JSON.stringify(data.map(simple)) !== JSON.stringify(draft.map(simple));

  async function guardar() {
    setSaving(true); setError(null); setExito(false);
    try {
      // Solo enviar los cambios reales (no obligatorios)
      const cambios = draft
        .filter((p) => p.puede_silenciar)
        .map((p) => ({
          tipo: p.tipo,
          email_activado: !!p.email_activado,
          inapp_activado: !!p.inapp_activado,
        }));
      await actualizarPreferenciasNotif(cambios);
      setData(draft.map((p) => ({ ...p })));
      setExito(true);
      setTimeout(() => setExito(false), 3000);
    } catch (e) {
      console.error('[guardar prefs]', e);
      setError(e?.response?.data?.error || 'No se pudieron guardar las preferencias.');
    } finally {
      setSaving(false);
    }
  }

  // Agrupar visualmente: silenciables arriba, obligatorias abajo
  const silenciables = draft.filter((p) => p.puede_silenciar);
  const obligatorias = draft.filter((p) => !p.puede_silenciar);

  return (
    <div className="min-h-full">
      {/* MINI-HEADER STICKY */}
      <div className="sticky top-14 z-30 bg-white/95 backdrop-blur-sm border-b border-slate-200">
        <div className="px-6 py-3 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              onClick={() => navigate(-1)}
              title="Volver"
              className="w-8 h-8 grid place-items-center rounded-md hover:bg-slate-100 text-slate-500"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <Bell className="w-5 h-5 text-slate-400 shrink-0" />
            <h1 className="text-sm font-medium text-slate-900">
              Preferencias de notificaciones
            </h1>
          </div>

          <div className="flex items-center gap-2">
            {exito && (
              <span className="text-xs text-emerald-600 font-medium flex items-center gap-1">
                <CheckCircle2 className="w-3.5 h-3.5" />
                Preferencias guardadas
              </span>
            )}
            <Button
              size="sm"
              onClick={guardar}
              disabled={!dirty || saving || loading}
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              Guardar
            </Button>
          </div>
        </div>
      </div>

      <div className="px-6 py-6 max-w-4xl">
        <p className="text-sm text-slate-600 mb-6">
          Decide qué notificaciones quieres recibir y por qué canal. Los cambios
          se aplican a partir del momento de guardar — las notificaciones ya
          enviadas no se modifican.
        </p>

        {error && (
          <div className="mb-4 border border-red-200 bg-red-50 rounded-lg p-3 text-red-700 text-sm flex items-start gap-2">
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            {error}
          </div>
        )}

        {loading ? (
          <div className="py-16 text-center text-sm text-slate-500">
            <Loader2 className="w-5 h-5 inline-block animate-spin mr-2" />
            Cargando preferencias…
          </div>
        ) : draft.length === 0 ? (
          <Card className="p-8 text-center">
            <p className="text-sm text-slate-500">
              No hay tipos de notificación configurables aún.
            </p>
          </Card>
        ) : (
          <>
            {silenciables.length > 0 && (
              <SeccionPreferencias
                titulo="Configurables"
                items={silenciables}
                onActualizar={actualizar}
              />
            )}

            {obligatorias.length > 0 && (
              <div className="mt-8">
                <SeccionPreferencias
                  titulo="Obligatorias"
                  hint="Estas notificaciones son críticas y no pueden silenciarse. Garantizan que recibes información esencial sobre tus citas."
                  items={obligatorias}
                  obligatorias={true}
                  onActualizar={actualizar}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function SeccionPreferencias({ titulo, hint, items, obligatorias = false, onActualizar }) {
  return (
    <div>
      <div className="mb-3">
        <h2 className="text-xs font-medium text-slate-500 uppercase tracking-wider">
          {titulo}
        </h2>
        {hint && (
          <p className="text-[11px] text-slate-400 mt-1 max-w-2xl">{hint}</p>
        )}
      </div>

      <Card className="overflow-hidden p-0">
        {/* Cabecera */}
        <div className="px-5 py-2.5 border-b border-slate-100 bg-slate-50 grid grid-cols-[1fr_80px_80px] gap-3 text-[10px] uppercase tracking-wider text-slate-500">
          <div>Notificación</div>
          <div className="text-center flex items-center justify-center gap-1">
            <Mail className="w-3 h-3" />
            Email
          </div>
          <div className="text-center flex items-center justify-center gap-1">
            <Bell className="w-3 h-3" />
            In-app
          </div>
        </div>

        <ul className="divide-y divide-slate-100">
          {items.map((p) => (
            <li
              key={p.tipo}
              className="px-5 py-3.5 grid grid-cols-[1fr_80px_80px] gap-3 items-center"
            >
              <div className="min-w-0">
                <div className="text-sm text-slate-900 flex items-center gap-2 flex-wrap">
                  {humanizarTipo(p.tipo)}
                  {obligatorias && (
                    <Badge variant="warning">
                      <Lock className="w-3 h-3" />
                      Obligatoria
                    </Badge>
                  )}
                </div>
                {p.descripcion && (
                  <p className="text-[11px] text-slate-500 mt-0.5">
                    {p.descripcion}
                  </p>
                )}
              </div>

              <div className="flex justify-center">
                <Switch
                  checked={!!p.email_activado}
                  disabled={obligatorias}
                  onChange={(v) => onActualizar(p.tipo, 'email_activado', v)}
                />
              </div>

              <div className="flex justify-center">
                <Switch
                  checked={!!p.inapp_activado}
                  disabled={obligatorias}
                  onChange={(v) => onActualizar(p.tipo, 'inapp_activado', v)}
                />
              </div>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}

function Switch({ checked, onChange, disabled }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
      className={
        'relative w-9 h-5 rounded-full transition-colors ' +
        (disabled
          ? 'bg-slate-200 cursor-not-allowed'
          : checked
            ? 'bg-primary cursor-pointer'
            : 'bg-slate-300 cursor-pointer hover:bg-slate-400')
      }
    >
      <span
        className={
          'absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-transform ' +
          (checked ? 'translate-x-4' : 'translate-x-0')
        }
      />
    </button>
  );
}

function simple(p) {
  return {
    tipo: p.tipo,
    email_activado: !!p.email_activado,
    inapp_activado: !!p.inapp_activado,
  };
}

/**
 * Convierte un tipo (CITA_RESERVADA_USUARIO) en texto humano.
 * Si la descripción de la plantilla es buena, se usa eso. Si no,
 * fallback a este mapeo.
 */
function humanizarTipo(tipo) {
  const MAPEO = {
    CITA_RESERVADA_USUARIO: 'Confirmación al reservar una cita',
    CITA_CANCELADA_ADMIN_USUARIO: 'Tu cita ha sido cancelada por administración',
    CITA_NO_ASISTIO_USUARIO: 'No asististe a tu cita',
    CAMPANA_RECORDATORIO_SIN_CITA_USUARIO: 'Recordatorio para reservar tu cita',
    USUARIO_NUEVA_CAMPANA: 'Te han añadido a una campaña',
    CITA_NUEVA_TECNICO: 'Nueva cita asignada (técnico)',
    CITA_CANCELADA_USUARIO_TECNICO: 'Un usuario canceló su cita (técnico)',
    CITA_CANCELADA_ADMIN_TECNICO: 'Cita cancelada por administración (técnico)',
    CITA_RECORDATORIO_24H_USUARIO: 'Recordatorio 24h antes de tu cita',
    CITA_RECORDATORIO_24H_TECNICO: 'Recordatorio 24h antes (técnico)',
    CITA_TRASPASADA_TECNICO: 'Te han traspasado una cita',
    // Fase 19b · #18 — tipos SOP que también llegan a un técnico con tickets
    // asignados (el sufijo _TECNICO es de audiencia, no de módulo).
    SOP_ASIGNADO_TECNICO: 'Ticket asignado',
    SOP_COMENTARIO_TECNICO: 'Nuevo comentario en tu ticket',
    ADMIN_RESUMEN_DIARIO: 'Resumen diario para administradores',
  };
  return MAPEO[tipo] || tipo;
}
